/**
 * Custom hook for Factory state management
 * Extraído de App.js para mejor mantenibilidad
 */
import { useState, useCallback, useEffect, useRef } from "react";
import api from "../lib/axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export function useFactoryState() {
  // Factory authentication
  const [factoryAuthenticated, setFactoryAuthenticated] = useState(false);
  const [factoryPassword, setFactoryPassword] = useState("");
  const factoryPasswordRef = useRef(""); // Ref to avoid stale closures
  
  // Update ref when password changes
  useEffect(() => {
    factoryPasswordRef.current = factoryPassword;
  }, [factoryPassword]);
  
  // Factory data
  const [factoryTypes, setFactoryTypes] = useState([]);
  const [factoryStats, setFactoryStats] = useState(null);
  const [factoryStatsByFactory, setFactoryStatsByFactory] = useState([]);
  const [availableFactories, setAvailableFactories] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [allTypeNames, setAllTypeNames] = useState([]);
  
  // Factory filters
  const [factoryFilterFactory, setFactoryFilterFactory] = useState("all");
  const [factoryFilterMaterial, setFactoryFilterMaterial] = useState("all");
  const [factoryFilterColor, setFactoryFilterColor] = useState("all");
  const [factoryFilterGlass, setFactoryFilterGlass] = useState("all");
  const [factoryFilterExtras, setFactoryFilterExtras] = useState("all");
  const [factoryFilterProject, setFactoryFilterProject] = useState("all");
  const [factorySearchQuery, setFactorySearchQuery] = useState("");
  const [factoryDimensionUnit, setFactoryDimensionUnit] = useState("m");
  
  // Factory parsed items modal state
  const [isFactoryParsedItemsOpen, setIsFactoryParsedItemsOpen] = useState(false);
  const [factoryParsedItems, setFactoryParsedItems] = useState([]);
  const [factoryParsedFilename, setFactoryParsedFilename] = useState("");
  const [factoryProjectName, setFactoryProjectName] = useState("");
  const [factoryInvoiceNo, setFactoryInvoiceNo] = useState("");
  const [factoryAllImages, setFactoryAllImages] = useState([]);
  const [factoryImageSelectorOpen, setFactoryImageSelectorOpen] = useState(null);
  
  // Bulk edit state
  const [factoryBulkMaterial, setFactoryBulkMaterial] = useState("");
  const [factoryBulkColor, setFactoryBulkColor] = useState("");
  const [factoryBulkFactory, setFactoryBulkFactory] = useState("");
  
  // Image suggestions
  const [imageSuggestionsOpen, setImageSuggestionsOpen] = useState(null);
  const [imageSuggestions, setImageSuggestions] = useState([]);
  
  // Adjustment state
  const [factoryAdjustPercent, setFactoryAdjustPercent] = useState("");
  const [factoryAdjustApplied, setFactoryAdjustApplied] = useState(false);
  
  // Factory manual add modal state
  const [isFactoryManualAddOpen, setIsFactoryManualAddOpen] = useState(false);
  const [factoryManualItem, setFactoryManualItem] = useState({
    name: "",
    material: "Aluminio",
    color: "",
    factory_name: "",
    project_name: "",
    width: "",
    height: "",
    cost: "",
    image_data: null,
    extra_specs: ""
  });
  const [factoryManualDragActive, setFactoryManualDragActive] = useState(false);
  
  // Factory edit/delete/duplicate modal state
  const [factoryEditType, setFactoryEditType] = useState(null);
  const [isFactoryEditOpen, setIsFactoryEditOpen] = useState(false);
  const [factoryDeleteType, setFactoryDeleteType] = useState(null);
  const [isFactoryDeleteOpen, setIsFactoryDeleteOpen] = useState(false);
  const [factoryDuplicateType, setFactoryDuplicateType] = useState(null);
  const [factoryDuplicateName, setFactoryDuplicateName] = useState("");
  const [isFactoryDuplicateOpen, setIsFactoryDuplicateOpen] = useState(false);
  
  // Upload state
  const [isFactoryUploading, setIsFactoryUploading] = useState(false);

  // Factory authentication handler - sets auth state, data loading happens via useEffect
  const handleFactoryAuth = useCallback(async () => {
    try {
      const response = await api.post(`${API}/factory/auth`, { password: factoryPassword });
      if (response.data.authenticated) {
        factoryPasswordRef.current = factoryPassword; // Update ref immediately
        setFactoryAuthenticated(true);
        toast.success("Acceso concedido a Costos de Fábrica");
        return factoryPassword;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Contraseña incorrecta");
      setFactoryPassword("");
      return null;
    }
  }, [factoryPassword]);
  
  // Pagination state for lazy loading
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreData, setHasMoreData] = useState(true);
  
  // Build query params from filters (SEC-002: password moved to header)
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    // SEC-002: Password is now sent via X-Factory-Password header, not query param
    
    if (factoryFilterMaterial && factoryFilterMaterial !== 'all') {
      params.append('material', factoryFilterMaterial);
    }
    if (factoryFilterFactory && factoryFilterFactory !== 'all') {
      params.append('factory_name', factoryFilterFactory);
    }
    if (factoryFilterColor && factoryFilterColor !== 'all') {
      params.append('color', factoryFilterColor);
    }
    if (factoryFilterGlass && factoryFilterGlass !== 'all') {
      params.append('glass_spec', factoryFilterGlass);
    }
    if (factoryFilterExtras && factoryFilterExtras !== 'all') {
      params.append('extra_specs', factoryFilterExtras);
    }
    if (factoryFilterProject && factoryFilterProject !== 'all') {
      params.append('project_name', factoryFilterProject);
    }
    if (factorySearchQuery && factorySearchQuery.trim()) {
      params.append('search', factorySearchQuery.trim());
    }
    
    return params;
  }, [factoryFilterMaterial, factoryFilterFactory, factoryFilterColor, factoryFilterGlass, factoryFilterExtras, factoryFilterProject, factorySearchQuery]);
  
  // SEC-002 FIX: Helper to get auth headers with factory password
  const getFactoryAuthHeaders = useCallback(() => {
    return {
      'X-Factory-Password': factoryPasswordRef.current
    };
  }, []);
  
  // Load factory types with server-side filtering (initial load or filter change)
  const loadFactoryTypes = useCallback(async (resetPage = true) => {
    const pwd = factoryPasswordRef.current;
    if (!pwd || !factoryAuthenticated) return;
    
    try {
      const params = buildFilterParams();
      const page = resetPage ? 1 : currentPage;
      params.append('page', page.toString());
      params.append('limit', '100'); // Load 100 items at a time for lazy loading
      
      // SEC-002 FIX: Send password via header instead of query param
      const response = await api.get(`${API}/factory/types?${params.toString()}`, {
        headers: getFactoryAuthHeaders()
      });
      const data = response.data;
      
      if (resetPage) {
        setFactoryTypes(data.types || []);
        setCurrentPage(1);
      } else {
        setFactoryTypes(prev => [...prev, ...(data.types || [])]);
      }
      
      setTotalPages(data.pages || 1);
      setTotalItems(data.total || 0);
      setHasMoreData(page < (data.pages || 1));
    } catch (error) {
      console.error("[Factory] Error loading types:", error?.message || error);
    }
  }, [buildFilterParams, factoryAuthenticated, currentPage, getFactoryAuthHeaders]);
  
  // Load more items (lazy loading)
  const loadMoreFactoryTypes = useCallback(async () => {
    if (isLoadingMore || !hasMoreData) return;
    
    setIsLoadingMore(true);
    try {
      const params = buildFilterParams();
      const nextPage = currentPage + 1;
      params.append('page', nextPage.toString());
      params.append('limit', '100');
      
      // SEC-002 FIX: Send password via header
      const response = await api.get(`${API}/factory/types?${params.toString()}`, {
        headers: getFactoryAuthHeaders()
      });
      const data = response.data;
      
      setFactoryTypes(prev => [...prev, ...(data.types || [])]);
      setCurrentPage(nextPage);
      setHasMoreData(nextPage < (data.pages || 1));
    } catch (error) {
      console.error("[Factory] Error loading more types:", error?.message || error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [buildFilterParams, currentPage, isLoadingMore, hasMoreData, getFactoryAuthHeaders]);
  
  // Auto-load factory data when authenticated
  useEffect(() => {
    if (factoryAuthenticated && factoryPasswordRef.current) {
      const loadInitialData = async () => {
        const authHeaders = { 'X-Factory-Password': factoryPasswordRef.current };
        
        try {
          // SEC-002 FIX: Load stats and first page of types using headers instead of query params
          const [statsRes, typesRes, factoriesRes, statsByFactoryRes, projectsRes, typeNamesRes] = await Promise.all([
            api.get(`${API}/factory/stats`, { headers: authHeaders }),
            api.get(`${API}/factory/types?page=1&limit=100`, { headers: authHeaders }),
            api.get(`${API}/factory/factories`, { headers: authHeaders }),
            api.get(`${API}/factory/stats-by-factory`, { headers: authHeaders }),
            api.get(`${API}/all-projects`),
            api.get(`${API}/type-names`)
          ]);
          
          setFactoryStats(statsRes.data || null);
          setFactoryTypes(typesRes.data?.types || []);
          setTotalPages(typesRes.data?.pages || 1);
          setTotalItems(typesRes.data?.total || 0);
          setHasMoreData(1 < (typesRes.data?.pages || 1));
          setCurrentPage(1);
          
          setAvailableFactories(factoriesRes.data?.factories || []);
          setFactoryStatsByFactory(statsByFactoryRes.data?.factories || []);
          setAllProjects(projectsRes.data?.projects || []);
          setAllTypeNames(typeNamesRes.data?.names || []);
        } catch (fetchError) {
          console.error("[Factory] Error loading data:", fetchError?.message || fetchError);
          toast.error("Error al cargar los datos de fábrica");
        }
      };
      
      loadInitialData();
    }
  }, [factoryAuthenticated]);
  
  // Reload types when filters change (debounced via FactoryView)
  const reloadFactoryTypes = useCallback(async () => {
    await loadFactoryTypes(true);
  }, [loadFactoryTypes]);

  // Fetch factory data - Complete version with all data (optimized with pagination)
  const fetchFactoryData = useCallback(async (password = null) => {
    const pwd = password || factoryPassword;
    if (!pwd) return;
    
    try {
      // First load stats and first page of types
      const authHeaders = { 'X-Factory-Password': pwd };
      const [statsRes, typesRes, factoriesRes, statsByFactoryRes, projectsRes, typeNamesRes] = await Promise.all([
        api.get(`${API}/factory/stats`, { headers: authHeaders }),
        api.get(`${API}/factory/types?page=1&limit=100`, { headers: authHeaders }),
        api.get(`${API}/factory/factories`, { headers: authHeaders }),
        api.get(`${API}/factory/stats-by-factory`, { headers: authHeaders }),
        api.get(`${API}/all-projects`),
        api.get(`${API}/type-names`)
      ]);
      
      // Set initial data
      setFactoryStats(statsRes.data);
      setAvailableFactories(factoriesRes.data?.factories || []);
      setFactoryStatsByFactory(statsByFactoryRes.data?.stats || []);
      setAllProjects(projectsRes.data?.projects || []);
      setAllTypeNames(typeNamesRes.data?.names || []);
      
      // Handle paginated types response (only first page for lazy loading)
      setFactoryTypes(typesRes.data?.types || []);
      setTotalPages(typesRes.data?.pages || 1);
      setTotalItems(typesRes.data?.total || 0);
      setHasMoreData(1 < (typesRes.data?.pages || 1));
      setCurrentPage(1);
    } catch (error) {
      console.error("Error fetching factory data:", error?.message || error);
    }
  }, [factoryPassword]);

  // Load image suggestions based on item text
  const loadImageSuggestions = useCallback(async (itemId, searchText) => {
    const pwd = factoryPasswordRef.current;
    if (!pwd) return;
    
    try {
      // P1 FIX: Correct parameter names for API endpoint
      const response = await api.get(`${API}/factory/image-suggestions`, {
        params: { text: searchText },
        headers: { 'X-Factory-Password': pwd }
      });
      setImageSuggestions(response.data.suggestions || []);
      setImageSuggestionsOpen(itemId);
    } catch (error) {
      console.error("Error loading image suggestions:", error?.message || error);
      setImageSuggestions([]);
    }
  }, []);

  // Load all projects
  const loadAllProjects = useCallback(async () => {
    try {
      const response = await api.get(`${API}/all-projects`);
      setAllProjects(response.data.projects || []);
    } catch (error) {
      console.error("Error loading projects:", error?.message || error);
    }
  }, []);

  // Load all type names for autocomplete
  const loadAllTypeNames = useCallback(async () => {
    try {
      const response = await api.get(`${API}/type-names`);
      setAllTypeNames(response.data.names || []);
    } catch (error) {
      console.error("Error loading type names:", error?.message || error);
    }
  }, []);

  // Clear factory filters
  const clearFactoryFilters = useCallback(() => {
    setFactoryFilterFactory("all");
    setFactoryFilterMaterial("all");
    setFactoryFilterColor("all");
    setFactoryFilterGlass("all");
    setFactoryFilterExtras("all");
    setFactoryFilterProject("all");
    setFactorySearchQuery("");
  }, []);

  // Reset factory manual item
  const resetFactoryManualItem = useCallback(() => {
    setFactoryManualItem({
      name: "",
      material: "Aluminio",
      color: "",
      factory_name: "",
      project_name: "",
      width: "",
      height: "",
      cost: "",
      image_data: null,
      extra_specs: ""
    });
  }, []);

  // Open factory edit modal
  const openFactoryEditModal = useCallback((type) => {
    setFactoryEditType(type);
    setIsFactoryEditOpen(true);
  }, []);

  // Open factory duplicate modal
  const openFactoryDuplicateModal = useCallback((type) => {
    setFactoryDuplicateType(type);
    setFactoryDuplicateName(type.name + " (copia)");
    setIsFactoryDuplicateOpen(true);
  }, []);

  // Open factory delete modal
  const openFactoryDeleteModal = useCallback((type) => {
    setFactoryDeleteType(type);
    setIsFactoryDeleteOpen(true);
  }, []);

  return {
    // Auth state
    factoryAuthenticated,
    setFactoryAuthenticated,
    factoryPassword,
    setFactoryPassword,
    handleFactoryAuth,
    
    // Data state
    factoryTypes,
    setFactoryTypes,
    factoryStats,
    setFactoryStats,
    factoryStatsByFactory,
    setFactoryStatsByFactory,
    availableFactories,
    setAvailableFactories,
    allProjects,
    setAllProjects,
    allTypeNames,
    setAllTypeNames,
    fetchFactoryData,
    loadAllProjects,
    loadAllTypeNames,
    
    // Lazy loading state
    currentPage,
    totalPages,
    totalItems,
    isLoadingMore,
    hasMoreData,
    loadMoreFactoryTypes,
    reloadFactoryTypes,
    
    // Filter state
    factoryFilterFactory,
    setFactoryFilterFactory,
    factoryFilterMaterial,
    setFactoryFilterMaterial,
    factoryFilterColor,
    setFactoryFilterColor,
    factoryFilterGlass,
    setFactoryFilterGlass,
    factoryFilterExtras,
    setFactoryFilterExtras,
    factoryFilterProject,
    setFactoryFilterProject,
    factorySearchQuery,
    setFactorySearchQuery,
    factoryDimensionUnit,
    setFactoryDimensionUnit,
    clearFactoryFilters,
    
    // Parsed items modal state
    isFactoryParsedItemsOpen,
    setIsFactoryParsedItemsOpen,
    factoryParsedItems,
    setFactoryParsedItems,
    factoryParsedFilename,
    setFactoryParsedFilename,
    factoryProjectName,
    setFactoryProjectName,
    factoryInvoiceNo,
    setFactoryInvoiceNo,
    factoryAllImages,
    setFactoryAllImages,
    factoryImageSelectorOpen,
    setFactoryImageSelectorOpen,
    
    // Bulk edit state
    factoryBulkMaterial,
    setFactoryBulkMaterial,
    factoryBulkColor,
    setFactoryBulkColor,
    factoryBulkFactory,
    setFactoryBulkFactory,
    
    // Image suggestions
    imageSuggestionsOpen,
    setImageSuggestionsOpen,
    imageSuggestions,
    setImageSuggestions,
    loadImageSuggestions,
    
    // Adjustment state
    factoryAdjustPercent,
    setFactoryAdjustPercent,
    factoryAdjustApplied,
    setFactoryAdjustApplied,
    
    // Manual add modal state
    isFactoryManualAddOpen,
    setIsFactoryManualAddOpen,
    factoryManualItem,
    setFactoryManualItem,
    factoryManualDragActive,
    setFactoryManualDragActive,
    resetFactoryManualItem,
    
    // Edit/Delete/Duplicate modal state
    factoryEditType,
    setFactoryEditType,
    isFactoryEditOpen,
    setIsFactoryEditOpen,
    factoryDeleteType,
    setFactoryDeleteType,
    isFactoryDeleteOpen,
    setIsFactoryDeleteOpen,
    factoryDuplicateType,
    setFactoryDuplicateType,
    factoryDuplicateName,
    setFactoryDuplicateName,
    isFactoryDuplicateOpen,
    setIsFactoryDuplicateOpen,
    
    // Modal open helpers
    openFactoryEditModal,
    openFactoryDuplicateModal,
    openFactoryDeleteModal,
    
    // Upload state
    isFactoryUploading,
    setIsFactoryUploading
  };
}

export default useFactoryState;
